import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import DocumentLink from '../../components/DocumentLink.jsx'
import { useDebounce } from '../../hooks/useDebounce.js'
import { statusColor, formatDate, dobAge, retirementNote, firstError, PNG_EARLY_MEDICAL_RETIREMENT_AGE } from '../../utils/format.js'
import { listPatients, formatMrn } from '../../api/patients.js'
import {
  listScreeningCampaigns,
  getScreeningCampaign,
  getScreeningProtocol,
  listScreeningRecords,
  startScreeningEncounter,
  updateScreeningRecord,
  generateFitnessReport,
  generateEmployerCertificate,
  listDoctorsForScreening,
} from '../../api/screening.js'

/**
 * Doctor-facing routine medical screening.
 *
 * The /superuser/screening surface (admin-only) owns the things only a
 * platform admin may do: creating campaigns, editing protocols, deleting
 * records and reading the PHI-safe cohort report. This module is the
 * other half — what a *doctor* needs to actually run a screening:
 *
 *   * see the campaigns that exist,
 *   * screen a patient in (the intake — opens a clinical encounter),
 *   * jump into that encounter to record the clinical findings,
 *   * record the fitness-for-work outcome and pull the Fitness PDF.
 *
 * The screening API already allows all of this for a doctor
 * (`start_encounter` and the record PATCH are `IsDoctor | IsAdminRole`);
 * this file is purely the missing doctor UI. Nothing admin-only is
 * reachable here — no campaign create/edit, no protocol editor, no
 * cohort report.
 */

const FITNESS_OPTIONS = [
  ['', 'Not recorded'],
  ['fit', 'Fit for work'],
  ['fit_with_restrictions', 'Fit with restrictions'],
  ['unfit', 'Unfit for work'],
  ['refer', 'Refer for further assessment'],
]

// ===========================================================================
// Campaign list — rendered as the "Screening" tab on the doctor dashboard.
// ===========================================================================

/**
 * The list of screening campaigns a doctor can work on. Campaigns are
 * created by the platform admin; the doctor opens one to screen patients
 * into it.
 */
export function DoctorScreeningList() {
  const campaignsQ = useQuery({
    queryKey: ['doctor-screening-campaigns'],
    queryFn: () => listScreeningCampaigns(),
  })
  const campaigns = campaignsQ.data || []

  return (
    <Card>
      <CardHeader
        title="Routine medical screening"
        subtitle="Campaigns are set up by the platform admin. Open one to screen patients in, complete their encounter, and record the fitness-for-work outcome."
      />
      {campaignsQ.error && (
        <ErrorBanner error={campaignsQ.error} onRetry={() => campaignsQ.refetch()} />
      )}
      {campaignsQ.isLoading && (
        <div className="text-sm text-slate-400 py-4">Loading campaigns…</div>
      )}
      {campaignsQ.data && (
        campaigns.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            No screening campaigns assigned to you. Campaigns are created and
            assigned by the platform admin — if you believe you should have
            access, please contact them.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2">Campaign</th>
                  <th className="text-left p-2">Organisation</th>
                  <th className="text-left p-2">Protocol</th>
                  <th className="text-left p-2 w-28">Status</th>
                  <th className="text-right p-2 w-24">Screened</th>
                  <th className="text-left p-2 w-28">Start</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-sand-50/50">
                    <td className="p-2">
                      <Link
                        to={`/doctor/screening/campaigns/${c.id}`}
                        className="font-medium text-wantok-700 hover:underline"
                      >
                        {c.name}
                      </Link>
                    </td>
                    <td className="p-2 text-slate-600">{c.customer_organization_name}</td>
                    <td className="p-2 text-slate-600">{c.protocol_name}</td>
                    <td className="p-2">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${statusColor(c.status)}`}>
                        {c.status_display || c.status}
                      </span>
                    </td>
                    <td className="p-2 text-right tabular-nums text-slate-700">
                      {c.screened_count}{c.expected_headcount ? ` / ${c.expected_headcount}` : ''}
                    </td>
                    <td className="p-2 text-slate-600">
                      {c.scheduled_start ? formatDate(c.scheduled_start) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Card>
  )
}

// ===========================================================================
// Campaign detail — the doctor's working surface for one campaign.
// ===========================================================================

/**
 * /doctor/screening/campaigns/:id — one campaign, doctor view. Read-only
 * campaign header plus the people screened, with intake + fitness-outcome
 * controls. No cohort report and no campaign edit/delete — those are
 * admin-only and stay on /superuser/screening.
 */
export default function DoctorScreeningCampaignDetail() {
  const { id } = useParams()
  const campaignQ = useQuery({
    queryKey: ['doctor-screening-campaign', id],
    queryFn: () => getScreeningCampaign(id),
    enabled: Boolean(id),
  })
  const campaign = campaignQ.data

  return (
    <div className="space-y-4">
      <Link to="/doctor" className="text-xs text-wantok-700 hover:underline">
        ← Back to screening
      </Link>

      {campaignQ.error && (
        <ErrorBanner error={campaignQ.error} onRetry={() => campaignQ.refetch()} />
      )}
      {campaignQ.isLoading && (
        <Card><div className="text-sm text-slate-400">Loading campaign…</div></Card>
      )}

      {campaign && (
        <>
          <Card>
            <CardHeader
              title={campaign.name}
              subtitle={`${campaign.customer_organization_name} · ${campaign.protocol_name}`}
              right={
                <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-medium ${statusColor(campaign.status)}`}>
                  {campaign.status_display || campaign.status}
                </span>
              }
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
              <Meta label="Delivery">{deliveryLabel(campaign.delivery_mode)}</Meta>
              <Meta label="Scheduled">
                {campaign.scheduled_start ? formatDate(campaign.scheduled_start) : '—'}
                {campaign.scheduled_end ? ` – ${formatDate(campaign.scheduled_end)}` : ''}
              </Meta>
              <Meta label="Expected">{campaign.expected_headcount || '—'}</Meta>
              <Meta label="Location">{campaign.location || '—'}</Meta>
            </div>
          </Card>

          <RecordsCard campaign={campaign} />
          <ProtocolChecklistCard
            protocolId={campaign.protocol}
            protocolName={campaign.protocol_name}
          />
        </>
      )}
    </div>
  )
}

// ===========================================================================
// People screened
// ===========================================================================

function RecordsCard({ campaign }) {
  const [showScreen, setShowScreen] = useState(false)
  const [fitnessFor, setFitnessFor] = useState(null)
  const recordsQ = useQuery({
    queryKey: ['doctor-screening-records', campaign.id],
    queryFn: () => listScreeningRecords({ campaign: campaign.id }),
    enabled: Boolean(campaign.id),
  })
  const records = recordsQ.data || []

  return (
    <Card>
      <CardHeader
        title="People screened"
        subtitle="Everyone screened into this campaign. Open an encounter to record the clinical findings."
        right={
          <button
            onClick={() => setShowScreen(true)}
            className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full bg-wantok-600 text-white hover:bg-wantok-700 transition-colors"
          >
            + Screen a patient
          </button>
        }
      />
      {recordsQ.error && (
        <ErrorBanner error={recordsQ.error} onRetry={() => recordsQ.refetch()} />
      )}
      {recordsQ.isLoading && <div className="text-sm text-slate-400 py-3">Loading…</div>}
      {recordsQ.data && (
        records.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            No one screened yet. Use “+ Screen a patient” to start.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2">Patient</th>
                  <th className="text-left p-2">Conducting doctor</th>
                  <th className="text-left p-2 w-24">Encounter</th>
                  <th className="text-left p-2 w-44">Fitness outcome</th>
                  <th className="p-2 w-56"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((rec) => (
                  <RecordRow key={rec.id} record={rec}
                    onFitness={() => setFitnessFor(rec)} />
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {showScreen && (
        <ScreenPatientModal campaign={campaign} onClose={() => setShowScreen(false)} />
      )}
      {fitnessFor && (
        <FitnessModal record={fitnessFor} onClose={() => setFitnessFor(null)} />
      )}
    </Card>
  )
}

function RecordRow({ record, onFitness }) {
  // Two documents, one encounter (occupational-health best practice):
  //  • employer certificate — diagnosis-free, the copy given to the employer
  //  • clinical record — the full report (diagnoses, findings), confidential
  const empMut = useMutation({
    mutationFn: () => generateEmployerCertificate(record.encounter, {}),
  })
  const clinMut = useMutation({
    mutationFn: () => generateFitnessReport(record.encounter, {}),
  })
  const fitnessLabel =
    (FITNESS_OPTIONS.find(([v]) => v === record.fitness_outcome) || [])[1]
    || 'Not recorded'

  return (
    <tr className="hover:bg-sand-50/50">
      <td className="p-2 text-slate-800">
        <div>{record.patient_name}</div>
        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
          {record.patient_dob && <span>{dobAge(record.patient_dob)}</span>}
          {record.patient_sex && <span className="capitalize">· {record.patient_sex}</span>}
          {record.patient != null && <span>· MRN <span className="font-mono">{formatMrn(record.patient)}</span></span>}
        </div>
      </td>
      <td className="p-2 text-slate-600">{record.doctor_name || '—'}</td>
      <td className="p-2 text-slate-500 text-xs">{record.encounter_status}</td>
      <td className="p-2">
        <span className={record.fitness_outcome ? 'text-slate-700' : 'text-slate-400 italic'}>
          {fitnessLabel}
        </span>
      </td>
      <td className="p-2 text-right space-x-2 whitespace-nowrap">
        {/* The core loop: the screening encounter is an ordinary CNE
            encounter — opening it drops the doctor into the usual editor
            to record vitals, diagnoses and orders. */}
        <Link
          to={`/doctor/encounters/${record.encounter}`}
          className="text-xs font-semibold text-wantok-700 hover:underline"
        >
          Open encounter
        </Link>
        <button onClick={onFitness}
          className="text-xs text-wantok-700 hover:underline">Record fitness</button>
        <button onClick={() => empMut.mutate()} disabled={empMut.isPending}
          title="Diagnosis-free certificate — the copy to give the employer"
          className="text-xs text-wantok-700 hover:underline disabled:opacity-50">
          {empMut.isPending ? 'Generating…' : 'Employer cert'}
        </button>
        {empMut.isSuccess && empMut.data?.id && (
          <DocumentLink doc={empMut.data} className="text-xs text-emerald-700 hover:underline" />
        )}
        {empMut.isError && (
          <span className="text-xs text-rose-600" title={String(empMut.error?.message || '')}>failed</span>
        )}
        <button onClick={() => clinMut.mutate()} disabled={clinMut.isPending}
          title="Full clinical record with diagnoses — for the clinical file, not the employer"
          className="text-xs text-slate-500 hover:underline disabled:opacity-50">
          {clinMut.isPending ? 'Generating…' : 'Clinical record'}
        </button>
        {clinMut.isSuccess && clinMut.data?.id && (
          <DocumentLink doc={clinMut.data} className="text-xs text-emerald-700 hover:underline" />
        )}
        {clinMut.isError && (
          <span className="text-xs text-rose-600" title={String(clinMut.error?.message || '')}>failed</span>
        )}
      </td>
    </tr>
  )
}

// ===========================================================================
// Screen a patient (intake)
// ===========================================================================

/**
 * Intake modal. Unlike the admin surface — which picks from the
 * organisation's roster — the doctor screens whoever is in front of them,
 * so this searches all patients. (Doctors can't read org staff links, and
 * the intake endpoint only needs a patient id.) The conducting doctor is
 * the signed-in doctor: `start_encounter` defaults `doctor` to the caller
 * when no `doctor_id` is sent.
 */
function ScreenPatientModal({ campaign, onClose }) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const debouncedQ = useDebounce(q, 250)
  const [selected, setSelected] = useState(null)
  const [dupWarn, setDupWarn] = useState(null)

  const canSearch = debouncedQ.trim().length >= 2
  const patientsQ = useQuery({
    queryKey: ['screening-patient-search', debouncedQ],
    queryFn: () => listPatients({ q: debouncedQ }),
    enabled: canSearch,
  })
  const results = patientsQ.data || []

  // Conducting doctor — blank means "me" (the backend defaults to the
  // signed-in user's doctor_profile). The picker lets an admin, or a
  // clinician whose doctor profile isn't resolvable, name someone explicitly
  // so intake never fails for an unresolved conducting doctor.
  const [doctorId, setDoctorId] = useState('')
  const doctorsQ = useQuery({
    queryKey: ['screening-doctors'],
    queryFn: () => listDoctorsForScreening(),
    staleTime: 5 * 60 * 1000,
  })
  const doctors = doctorsQ.data || []

  const mut = useMutation({
    mutationFn: (vars = {}) => startScreeningEncounter(campaign.id, {
      patient_id: selected.id,
      ...(doctorId ? { doctor_id: parseInt(doctorId, 10) } : {}),
      ...(vars.proceedAnyway ? { proceed_anyway: true } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-screening-records', campaign.id] })
      qc.invalidateQueries({ queryKey: ['doctor-screening-campaign', String(campaign.id)] })
      qc.invalidateQueries({ queryKey: ['doctor-screening-campaigns'] })
      onClose()
    },
    onError: (err) => {
      const d = err?.response?.data
      if (err?.response?.status === 409 && d?.code === 'possible_duplicate') {
        setDupWarn(d)
      }
    },
  })

  return (
    <Modal title="Screen a patient" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Find the patient in front of you. Screening opens a clinical
          encounter — by default with you as the conducting doctor (pick a
          different one below if needed). Complete it in the usual editor.
        </p>
        <Field label="Search patients">
          <input
            type="search" value={q} autoFocus
            onChange={(e) => { setQ(e.target.value); setSelected(null); setDupWarn(null) }}
            placeholder="Name, MRN or date of birth…"
            className={inputCls}
          />
        </Field>
        {!canSearch ? (
          <p className="text-xs text-slate-400 italic">
            Type at least 2 characters to search.
          </p>
        ) : patientsQ.isLoading ? (
          <p className="text-xs text-slate-400">Searching…</p>
        ) : patientsQ.error ? (
          <ErrorBanner error={patientsQ.error} onRetry={() => patientsQ.refetch()} />
        ) : results.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No patients match “{debouncedQ}”.
          </p>
        ) : (
          <ul className="max-h-52 overflow-y-auto divide-y divide-slate-100 border border-sand-200 rounded-lg">
            {results.map((p) => {
              const isSel = selected?.id === p.id
              return (
                <li key={p.id}>
                  <button
                    type="button" onClick={() => { setSelected(p); setDupWarn(null) }}
                    className={`w-full text-left px-3 py-2 ${isSel ? 'bg-wantok-50' : 'hover:bg-sand-50/60'}`}
                  >
                    <div className="text-sm font-medium text-slate-800">
                      {p.full_name || p.name || p.mrn || `Patient #${p.id}`}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                      {p.mrn && <span className="font-mono">{p.mrn}</span>}
                      {p.dob && <span>{dobAge(p.dob)}</span>}
                      {p.sex && <span className="capitalize">{p.sex}</span>}
                      {p.province && <span>{p.province}</span>}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        <Field label="Conducting doctor">
          <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
            className={inputCls}>
            <option value="">Me (signed-in doctor)</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>{doctorLabel(d)}</option>
            ))}
          </select>
        </Field>
        {dupWarn && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-800">Possible duplicate</p>
            <p className="text-xs text-amber-700 mt-1">{dupWarn.detail}</p>
            <ul className="text-xs text-amber-700 mt-1 list-disc pl-4">
              {(dupWarn.matches || []).map((m, i) => (
                <li key={i}>Already screened in this campaign — matched on {m.matched_on}.</li>
              ))}
            </ul>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={onClose}
                className="px-2 py-1 rounded-md border border-amber-300 text-xs text-amber-800 hover:bg-amber-100">
                Use existing — cancel
              </button>
              <button type="button" disabled={mut.isPending}
                onClick={() => { setDupWarn(null); mut.mutate({ proceedAnyway: true }) }}
                className="px-2 py-1 rounded-md bg-amber-600 text-white text-xs hover:bg-amber-700 disabled:opacity-50">
                Different person — screen anyway
              </button>
            </div>
          </div>
        )}
        {mut.error && !dupWarn && <FormError error={mut.error} />}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button" onClick={onClose} disabled={mut.isPending}
            className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50"
          >
            Cancel
          </button>
          <button
            type="button" onClick={() => mut.mutate()}
            disabled={mut.isPending || !selected}
            className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50"
          >
            {mut.isPending ? 'Screening…' : 'Screen patient'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ===========================================================================
// Fitness-for-work outcome
// ===========================================================================

function FitnessModal({ record, onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    fitness_outcome: record.fitness_outcome || '',
    fitness_restrictions: record.fitness_restrictions || '',
    fitness_valid_until: record.fitness_valid_until || '',
    significant_medical_condition: Boolean(record.significant_medical_condition),
  })
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const advisory = retirementNote(record.patient_dob, form.significant_medical_condition)

  const mut = useMutation({
    mutationFn: () => updateScreeningRecord(record.id, {
      fitness_outcome: form.fitness_outcome,
      fitness_restrictions: form.fitness_restrictions,
      fitness_valid_until: form.fitness_valid_until || null,
      significant_medical_condition: form.significant_medical_condition,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-screening-records', record.campaign] })
      onClose()
    },
  })

  return (
    <Modal title={`Fitness outcome — ${record.patient_name}`} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); mut.mutate() }} className="space-y-3">
        <Field label="Outcome">
          <select value={form.fitness_outcome}
            onChange={(e) => setF('fitness_outcome', e.target.value)}
            className={inputCls}>
            {FITNESS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <div className="rounded-md border border-sand-200 bg-sand-50/40 px-3 py-2 space-y-1">
          <div className="text-[11px] text-slate-600">
            {record.patient_dob ? dobAge(record.patient_dob) : 'Date of birth not on file'}
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-700">
            <input type="checkbox"
              checked={form.significant_medical_condition}
              onChange={(e) => setF('significant_medical_condition', e.target.checked)}
              className="mt-0.5" />
            <span>Significant medical condition present (enables early medical retirement from age {PNG_EARLY_MEDICAL_RETIREMENT_AGE})</span>
          </label>
          {advisory && (
            <div className={advisory.tone === 'warn'
              ? 'text-[11px] text-amber-700' : 'text-[11px] text-slate-500'}>
              {advisory.text}
            </div>
          )}
        </div>
        <Field label="Restrictions / accommodations">
          <textarea rows={2} value={form.fitness_restrictions}
            onChange={(e) => setF('fitness_restrictions', e.target.value)}
            placeholder="Only when the outcome is fit with restrictions"
            className={inputCls} />
        </Field>
        <Field label="Valid until (optional)">
          <input type="date" value={form.fitness_valid_until}
            onChange={(e) => setF('fitness_valid_until', e.target.value)}
            className={inputCls} />
        </Field>
        <p className="text-[11px] text-slate-500">
          The Fitness for Work PDF pulls this outcome automatically.
        </p>
        {mut.error && <FormError error={mut.error} />}
        <ModalActions onClose={onClose} submitting={mut.isPending}
          submitLabel="Save outcome" />
      </form>
    </Modal>
  )
}

// ===========================================================================
// What to capture — the protocol checklist, read-only reference
// ===========================================================================

// The five categories a protocol item can carry, in the order they read
// best on the page. `category` is a closed set on the model; the label
// map falls through to the raw value if a new one is ever added.
const ITEM_CATEGORY_LABELS = {
  vital: 'Vital signs',
  measurement: 'Measurements',
  lab_panel: 'Lab panels / tests',
  imaging: 'Imaging studies',
  question: 'Questions / observations',
}
const ITEM_CATEGORY_ORDER = ['vital', 'measurement', 'lab_panel', 'imaging', 'question']

/**
 * Read-only reference panel: the checklist from the campaign's protocol,
 * so the doctor knows what the screening should cover. The actual
 * readings are still recorded in the encounter (vitals, diagnoses,
 * orders) — a protocol item is a prompt, not a data field.
 */
function ProtocolChecklistCard({ protocolId, protocolName }) {
  const q = useQuery({
    queryKey: ['doctor-screening-protocol', protocolId],
    queryFn: () => getScreeningProtocol(protocolId),
    enabled: Boolean(protocolId),
  })
  const protocol = q.data
  const items = protocol?.items || []

  // Categories present, in canonical order, then any unknown ones last.
  const presentCats = ITEM_CATEGORY_ORDER.filter(
    (c) => items.some((it) => it.category === c),
  )
  const extraCats = [...new Set(items.map((it) => it.category))].filter(
    (c) => !ITEM_CATEGORY_ORDER.includes(c),
  )
  const orderedCats = [...presentCats, ...extraCats]

  return (
    <Card>
      <CardHeader
        title="What to capture"
        subtitle={`Checklist from the ${protocolName || 'screening'} protocol. Record the findings in the encounter as you go.`}
      />
      {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && (
        <div className="text-sm text-slate-400 py-3">Loading checklist…</div>
      )}
      {protocol && (
        items.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4 text-center">
            This protocol has no checklist items yet.
          </div>
        ) : (
          <div className="space-y-4">
            {protocol.description && (
              <p className="text-xs text-slate-500">{protocol.description}</p>
            )}
            {orderedCats.map((cat) => (
              <div key={cat}>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  {ITEM_CATEGORY_LABELS[cat] || cat}
                </div>
                <ul className="space-y-1">
                  {items
                    .filter((it) => it.category === cat)
                    .map((it) => (
                      <li key={it.id} className="flex items-baseline gap-2 text-sm">
                        <span className="text-wantok-400" aria-hidden>•</span>
                        <span className="text-slate-700">{it.label}</span>
                        {!it.is_required && (
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">
                            optional
                          </span>
                        )}
                        {it.guidance && (
                          <span className="text-xs text-slate-500">— {it.guidance}</span>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        )
      )}
    </Card>
  )
}

// ===========================================================================
// Shared building blocks
// ===========================================================================

const inputCls = 'w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white'

function doctorLabel(d) {
  return d.full_name || d.name || d.display_name
    || (d.user && (d.user.full_name || d.user.username))
    || `Doctor #${d.id}`
}

function deliveryLabel(mode) {
  return ({ site_visit: 'Site visit', clinic: 'Clinic', telehealth: 'Telehealth' })[mode]
    || mode || '—'
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

function FormError({ error }) {
  // 409 = the person is already enrolled; everything else gets the most
  // specific message we can extract from the normalised error envelope —
  // firstError digs into error.details so we never show a bare
  // "Validation failed".
  const msg = error?.response?.status === 409
    ? 'This patient has already been screened in this campaign — open their existing encounter from the list instead of screening them again.'
    : firstError(error, 'Could not screen this patient.')
  return (
    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
      {String(msg)}
    </div>
  )
}

function ModalActions({ onClose, submitting, submitLabel, disabled }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} disabled={submitting}
        className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50">
        Cancel
      </button>
      <button type="submit" disabled={submitting || disabled}
        className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50">
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  )
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-sand-200 w-full max-w-lg mt-12 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}
